import { type ReactNode, createElement } from "react";

import { UnreadChatsTourContent } from "@/components/chat/unread-chats-tour-content";

import { trainingTours } from "./training-tours";

export interface TourStep {
  icon: ReactNode | string | null;
  title: string;
  content: ReactNode;
  selector: string;
  side?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "left-top"
    | "left-bottom"
    | "right-top"
    | "right-bottom";
  showControls?: boolean;
  pointerPadding?: number;
  pointerRadius?: number;
  nextRoute?: string;
  prevRoute?: string;
}

export interface TourDefinition {
  tour: string;
  steps: TourStep[];
}

export const PHONE_NUMBER_CONFIG_TOUR = "phone-number-config";
export const FRONT_DESK_SCHEDULING_TOUR = "front-desk-scheduling";
// Launch guidance for the calendar's confirmation follow-up list. Both tours
// switch themselves off at CONFIRMATION_FOLLOW_UP_TOURS_SUNSET (see
// hooks/use-confirmation-follow-up-tours.ts); delete them after that date.
export const CALENDAR_FOLLOW_UP_BUTTON_TOUR = "calendar-follow-up-button";
export const CONFIRMATION_FOLLOW_UP_LIST_TOUR = "confirmation-follow-up-list";

// "-v2" so browsers that finished the first version also see the setting tip.
export const UNREAD_CHATS_CLEANUP_TOUR = "unread-chats-cleanup-v2";

/** Element the unread-chats tour points at: the chat list on the Chat page. */
export const UNREAD_CHATS_TOUR_TARGET_ID = "tour-unread-chats";

const featureTours: TourDefinition[] = [
  {
    tour: FRONT_DESK_SCHEDULING_TOUR,
    steps: [
      {
        icon: null,
        title: "Let's schedule an appointment",
        content:
          "You landed here by clicking Create Event on the calendar (or by clicking any empty time slot and choosing Appointment). Here's how to fill it out.",
        selector: "#tour-appt-header",
        side: "bottom",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Pick a patient",
        content:
          "Start by searching for the patient. Type a name, phone number, or date of birth and select the right person.",
        selector: "[data-field='patientId']",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Choose a provider",
        content:
          "Pick the provider who will see this patient. Only providers available at the selected location are shown.",
        selector: "#tour-appt-provider",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Add a service",
        content:
          "Click Add Service to choose what the visit is for. Each service brings its own default duration plus any prep or cleanup time.",
        selector: "#tour-appt-service",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Set the date and time",
        content:
          'Choose when the appointment happens. You can type natural language like "tomorrow" or "next week" in the date field.',
        selector: "#tour-appt-datetime",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Fine-tune on the timeline",
        content:
          "Once a service is added, adjust it here: drag a block up or down to change the start time, and drag its top or bottom edge to change the duration. Everything snaps to your calendar's time interval.",
        selector: "#tour-appt-timeline",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
    ],
  },
  {
    tour: PHONE_NUMBER_CONFIG_TOUR,
    steps: [
      {
        icon: null,
        title: "Phone Number Configuration",
        content:
          "This is where you control how inbound calls are routed for this number. Set a timezone, assign a location, and configure handlers below.",
        selector: "#tour-config-header",
        side: "bottom",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Timezone & Location",
        content:
          "Set the timezone used for all schedules on this number, and optionally link it to a location for reporting.",
        selector: "#tour-config-settings",
        side: "bottom",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Ring Groups",
        content:
          "Route calls to your team during scheduled hours. Add members, set a schedule, and calls will ring everyone in the group.",
        selector: "#tour-ring-groups",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "AI Assistants",
        content:
          "Schedule AI assistants to answer calls. Use them for after-hours coverage or to handle overflow during busy periods.",
        selector: "#tour-ai-assistants",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Closures",
        content:
          "Block call routing during holidays or shutdowns. Active closures override ring groups and scheduled assistants.",
        selector: "#tour-closures",
        side: "right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Weekly Schedule",
        content:
          "See all your ring groups, assistants, and closures mapped to the week. Click any empty cell to quickly create a new handler.",
        selector: "#tour-weekly-timeline",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Routing Priority",
        content:
          "Closures take priority, then scheduled AI assistants, then ring groups. Anything outside scheduled hours goes to the off-hours handler — and if no handler is set, the caller can leave a voicemail.",
        selector: "#tour-routing-priority",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
    ],
  },
  // TODO(2026-10-25): remove both follow-up tours, their hook and the
  // tour-follow-up-* ids once CONFIRMATION_FOLLOW_UP_TOURS_SUNSET has passed.
  {
    tour: CALENDAR_FOLLOW_UP_BUTTON_TOUR,
    steps: [
      {
        icon: null,
        title: "New: Follow-Up",
        content:
          "One place to get the day confirmed and fill any openings from the waitlist. The ring shows how much of the day is confirmed, and the number shows who needs you. Click it to open the panel.",
        selector: "#tour-follow-up-button",
        side: "bottom-right",
        pointerPadding: 8,
        pointerRadius: 8,
      },
    ],
  },
  {
    tour: CONFIRMATION_FOLLOW_UP_LIST_TOUR,
    steps: [
      {
        icon: null,
        title: "The day at a glance",
        content:
          "This line says how the day stands: what needs you, who is waiting on a reply, and how many are confirmed. To do lists only what needs you; Day shows every appointment. It follows the day shown on the calendar.",
        selector: "#tour-follow-up-glance",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Only who needs you",
        content:
          "Decoda's confirmation and reminder texts handle most patients on their own. This list shows only the ones that need a person: a text that bounced, no reply, or a reply Decoda wasn't sure about.",
        selector: "#tour-follow-up-needs-you",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "One next step",
        content:
          "Each row has one button that moves it forward, usually Call. When you hang up, a window asks how it went, with the answer from the call already selected. Check it and save. Click a row to see why it's here, the texts sent and other actions.",
        selector: "#tour-follow-up-next",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: "Fill openings from the waitlist",
        content:
          "When a patient can't make it, the time shows up under Openings with the best waitlist match and a button to book them. Click it to see everyone who fits or text them an offer. The full waitlist is in the Waitlist tab.",
        selector: "#tour-follow-up-needs-you",
        side: "left",
        pointerPadding: 8,
        pointerRadius: 8,
      },
    ],
  },
  {
    tour: UNREAD_CHATS_CLEANUP_TOUR,
    steps: [
      {
        icon: null,
        title: "Clear unread chats faster",
        content: createElement(UnreadChatsTourContent),
        selector: `#${UNREAD_CHATS_TOUR_TARGET_ID}`,
        side: "right",
        pointerPadding: 6,
        pointerRadius: 10,
      },
    ],
  },
];

export const allTours: TourDefinition[] = [...featureTours, ...trainingTours];
